// Settings.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import DataSync from "../Webflow/DataSync";
import Appearance from "../Webflow/Appearence";
import DefaultQuestions from "../Webflow/DefaultQuestions";
import RecaptchaSettings from "../Webflow/Recaptcha";
import EmailTemplate from "../Webflow/EmailTemplate";
import Users from "../Webflow/Users";
import Script from "../Webflow/Script";
import ExternalMedia from "../Webflow/ExternalVideos";
import Integrations from "../Webflow/Integrations";
import { useDomain } from "context/DomainContext";

import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  LinearProgress,
  Stack,
  Grid,
  Dialog,
  DialogContent,
  Button,
  Fade,
} from "@mui/material";

import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import GroupIcon from "@mui/icons-material/Group";
import ExtensionIcon from "@mui/icons-material/Extension";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CampaignIcon from "@mui/icons-material/Campaign";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSoftUIController } from "context";
import { setMiniSidenav } from "context";
import UserPricing from "../Webflow/UserPricing";
import { Payment } from "@mui/icons-material";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "constants";
import { useSettings } from "context/SettingsContext";
import WhitelabelBrandingPage from "../Webflow/WebsiteRebranding";
import { useSystemTheme } from "context/SystemThemeContext";

export default function Settings() {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";

  const { source } = useDomain();
  const [tabIndex, setTabIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav } = controller;
  const { user, isAuthenticated, getCurrentRole, loading, isInitialized } = useAuth();
  const role = getCurrentRole() || sessionStorage.getItem("role");
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const { settings, loading: settingsLoading } = useSettings();

  // Update check states
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Sync status states
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(true);
  const [pendingPagesCount, setPendingPagesCount] = useState(0);
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [pendingMediaCount, setPendingMediaCount] = useState(0);
  const [totalMediaCount, setTotalMediaCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  // Tab visibility tracking
  const isTabVisibleRef = useRef(!document.hidden);
  const pollingIntervalRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    setMiniSidenav(dispatch, !miniSidenav);
  }, []);

  useEffect(() => {
    if (isMobile) setCollapsed(false);
    else if (isTablet) setCollapsed(true);
  }, [isMobile, isTablet]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isNowVisible = !document.hidden;
      const wasVisible = isTabVisibleRef.current;

      isTabVisibleRef.current = isNowVisible;

      if (!wasVisible && isNowVisible && isPolling) {
        clearInterval(pollingIntervalRef.current);
        startPolling();
        fetchSyncStatus(false);
      } else if (wasVisible && !isNowVisible) {
        clearInterval(pollingIntervalRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPolling]);

  const checkForUpdate = async () => {
    if (!domainId || source !== "webflow") return;

    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/webflow/check-update`, {
        method: "GET",
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.result?.update_available) {
          setUpdateInfo(data.result);
          setUpdateModalOpen(true);
        }
      }
    } catch (error) {
      console.error("Error checking for update:", error);
    }
  };

  const handleUpdateNow = async () => {
    setUpdateLoading(true);
    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/webflow/update-script`, {
        method: "POST",
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateModalOpen(true);
          setUpdateSuccess(true);
          setUpdateInfo(null);
        }, 2500);
      }
    } catch (error) {
      console.error("Error updating script:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseUpdateModal = () => {
    if (updateLoading) return;
    setUpdateModalOpen(false);
    setUpdateSuccess(false);
  };

  const fetchSyncStatus = async (showLoading = true) => {
    if (!isTabVisibleRef.current && !showLoading) {
      return false;
    }

    if (showLoading) setSyncLoading(true);
    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/sync-status`, {
        method: "GET",
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);

        const posts = data?.result?.result?.posts || [];
        const pendingPages = posts.filter((post) => post.status === "pending").length;
        setTotalPagesCount(posts.length);
        setPendingPagesCount(pendingPages);

        const media = data?.result?.result?.media || {};
        const videos = media?.videos || [];
        const documents = media?.documents || [];
        const allMedia = [...videos, ...documents];
        const pendingMedia = allMedia.filter((item) => item.status === "pending").length;
        setTotalMediaCount(allMedia.length);
        setPendingMediaCount(pendingMedia);

        const hasPending = pendingPages > 0 || pendingMedia > 0;
        return hasPending;
      }
    } catch (error) {
      console.error("Error fetching sync status:", error);
    } finally {
      if (showLoading) setSyncLoading(false);
    }
    return false;
  };

  const startPolling = () => {
    if (!isTabVisibleRef.current) {
      return;
    }

    clearInterval(pollingIntervalRef.current);

    pollingIntervalRef.current = setInterval(async () => {
      if (!isTabVisibleRef.current) {
        return;
      }

      const hasPending = await fetchSyncStatus(false);
      if (!hasPending) {
        setIsPolling(false);
        clearInterval(pollingIntervalRef.current);
      }
    }, 10000);
  };

  useEffect(() => {
    if (isPolling) {
      startPolling();
    } else {
      clearInterval(pollingIntervalRef.current);
    }

    return () => {
      clearInterval(pollingIntervalRef.current);
    };
  }, [isPolling, domainId]);

  useEffect(() => {
    if (source && domainId) {
      fetchSyncStatus();
      checkForUpdate();
    }
  }, [source, domainId]);

  useEffect(() => {
    const handleSyncStarted = () => {
      setIsPolling(true);
      fetchSyncStatus(false);
    };

    window.addEventListener("syncStarted", handleSyncStarted);

    return () => {
      window.removeEventListener("syncStarted", handleSyncStarted);
    };
  }, []);

  const pagesProgress =
    totalPagesCount > 0 ? ((totalPagesCount - pendingPagesCount) / totalPagesCount) * 100 : 0;
  const mediaProgress =
    totalMediaCount > 0 ? ((totalMediaCount - pendingMediaCount) / totalMediaCount) * 100 : 0;

  const filteredTabs = useMemo(() => {
    if (!source) return [];

    let tabs = [
      { label: "Scripts", component: <Script />, icon: <CodeIcon fontSize="small" /> },
      { label: "Appearance", component: <Appearance />, icon: <ColorLensIcon fontSize="small" /> },
      { label: "Email template", component: <EmailTemplate />, icon: <EmailIcon fontSize="small" /> },
      {
        label: "Google Recaptcha",
        component: <RecaptchaSettings />,
        icon: <SecurityIcon fontSize="small" />,
      },
      { label: "Data Sync", component: <DataSync />, icon: <SyncIcon fontSize="small" /> },
      {
        label: "Default Questions",
        component: <DefaultQuestions />,
        icon: <HelpOutlineIcon fontSize="small" />,
      },
      {
        label: "External Media",
        component: <ExternalMedia />,
        icon: <VideoLibraryIcon fontSize="small" />,
      },
      { label: "Users", component: <Users />, icon: <GroupIcon fontSize="small" /> },
    ];

    if (source === "wordpress") {
      tabs = tabs.filter((tab) => tab.label !== "Data Sync");
    }
    if (source === "webflow" || source === "wordpress" || source === "") {
      tabs = tabs.filter((tab) => tab.label !== "Scripts");
    }
    if (role === "read&write") {
      tabs = tabs.filter((tab) => tab.label !== "Integrations");
    }
    if (role !== "admin") {
      tabs = tabs.filter((tab) => tab.label !== "Users");
    }

    return tabs;
  }, [source, role, settings]);

  useEffect(() => {
    if (!source) return;

    const savedTab = sessionStorage.getItem("settingsTabIndex");
    if (savedTab !== null && filteredTabs[savedTab]) {
      setTabIndex(Number(savedTab));
    } else {
      if (source === "custom" || source === "wix" || source === "squarespace") {
        const scriptsIndex = filteredTabs.findIndex((tab) => tab.label === "Scripts");
        if (scriptsIndex !== -1) setTabIndex(scriptsIndex);
        else setTabIndex(0);
      } else {
        setTabIndex(0);
      }
    }
  }, [source, filteredTabs]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabSelect = (index) => {
    setTabIndex(index);
    sessionStorage.setItem("settingsTabIndex", index);
    if (isMobile) setMobileOpen(false);
  };

  const SidebarContent = ({ isMobileDrawer = false }) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
        width: "100%",
        transition: "background-color 0.3s ease",
      }}
    >
      {isMobileDrawer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "1rem", fontWeight: 600, color: isDarkMode ? "#e2e8f0" : undefined }}
          >
            Settings
          </Typography>
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }} />
          </IconButton>
        </Box>
      )}

      {!isMobileDrawer && !isMobile && (
        <Box display="flex" justifyContent={collapsed ? "center" : "flex-end"} width="100%" p={1}>
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? (
              <MenuOpenIcon sx={{ color: isDarkMode ? "#e2e8f0" : undefined }} />
            ) : (
              <MenuIcon sx={{ color: isDarkMode ? "#e2e8f0" : undefined }} />
            )}
          </IconButton>
        </Box>
      )}

      <List sx={{ flexGrow: 1, width: "100%", px: isMobileDrawer ? 1 : 2 }}>
        {filteredTabs.map((tab, index) => (
          <Tooltip
            title={!isMobileDrawer && collapsed ? tab.label : ""}
            placement="right"
            key={tab.label}
          >
            <ListItemButton
              selected={tabIndex === index}
              onClick={() => handleTabSelect(index)}
              sx={{
                borderRadius: "8px",
                margin: "4px",
                paddingY: 1,
                paddingX: !isMobileDrawer && collapsed ? 1 : 2,
                color: isDarkMode ? "#e2e8f0" : undefined,
                "&.Mui-selected": {
                  backgroundColor: "#219297",
                  color: "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                  "&:hover": {
                    backgroundColor: "#39b1b8",
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  },
                },
                "&:hover": {
                  backgroundColor: isDarkMode ? "#2d2d3f" : "#39b1b8",
                  color: isDarkMode ? "#fff" : "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: !isMobileDrawer && collapsed ? "auto" : 38,
                  color: tabIndex === index ? "#fff" : isDarkMode ? "#a0aec0" : undefined,
                }}
              >
                {tab.icon}
              </ListItemIcon>
              {(isMobileDrawer || !collapsed) && (
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: isMobileDrawer ? "0.9rem" : "0.8rem",
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  if (!source || settingsLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "70vh" }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: "#219297",
              animationDuration: "800ms",
            }}
          />
        </SoftBox>
      </DashboardLayout>
    );
  }

  const showBanners = pendingPagesCount > 0 || pendingMediaCount > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />

<Dialog
  open={updateModalOpen}
  onClose={handleCloseUpdateModal}
  TransitionComponent={Fade}
  transitionDuration={300}
  PaperProps={{
    sx: {
      borderRadius: "16px",
      backgroundColor: isDarkMode ? "#0d1526" : "#ffffff",
      border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
      boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
      width: 400,
      maxWidth: "95vw",
      overflow: "hidden",
    },
  }}
>
  <DialogContent sx={{ p: 0 }}>
    {updateSuccess ? (
      <Fade in={updateSuccess}>
        <Box sx={{ py: 5, px: 4, textAlign: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "#0d2d1a" : "#e6faf3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              animation: "popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            }}
          >
            <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: isDarkMode ? "#e2e8f0" : "#1a1a1a", mb: 0.75 }}>
            Updated successfully
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: isDarkMode ? "#a0aec0" : "#777" }}>
            Your Webflow app is now on the latest version.
          </Typography>
        </Box>
      </Fade>

    ) : updateLoading ? (
      <Box sx={{ py: 4, px: 3, textAlign: "center" }}>
        <Box sx={{ position: "relative", width: 52, height: 52, margin: "0 auto 16px" }}>
          {/* <CircularProgress
            size={52}
            thickness={3}
            sx={{ color: "#219297", position: "absolute", top: 0, left: 0 }}
          /> */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SystemUpdateAltIcon sx={{ color: "#219297", fontSize: 22 }} />
          </Box>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: isDarkMode ? "#e2e8f0" : "#1a1a1a", mb: 0.75 }}>
          Updating...
        </Typography>
        {/* <Typography sx={{ fontSize: "0.82rem", color: isDarkMode ? "#a0aec0" : "#888", mb: 2 }}>
          Please don't close this window
        </Typography> */}
        <Box
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: isDarkMode ? "#2d2d3f" : "#e3fdf9",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: 2,
              backgroundColor: "#219297",
              animation: "indeterminate 1.6s ease-in-out infinite",
              width: "40%",
            }}
          />
        </Box>
      </Box>

    ) : (
      <>
        <Box
          sx={{
            backgroundColor: isDarkMode ? "#131d30" : "#f7fffe",
            borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e8f7f7"}`,
            py: 3,
            px: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "#1a2d3f" : "#e3fdf9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
            }}
          >
            <CampaignIcon sx={{ color: "#219297", fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: isDarkMode ? "#e2e8f0" : "#1a1a1a", mb: 0.5 }}>
            New update available
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: isDarkMode ? "#a0aec0" : "#777" }}>
            Version{" "}
            <Box component="span" sx={{ color: "#219297", fontWeight: 700 }}>
              v{updateInfo?.latest_version}
            </Box>{" "}
            is ready to install
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2.5, backgroundColor: isDarkMode ? "#1a2d3f" : "#f7fffe", }}>
          <Box
            sx={{
              backgroundColor: isDarkMode ? "#000000" : "#f7fffe",
              border: `1px solid ${isDarkMode ? "#2d2d3f" : "#d0f0f2"}`,
              borderRadius: "10px",
              px: 2,
              py: 1.5,
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: isDarkMode ? "#5a7a8a" : "#999",
                mb: 0.75,
              }}
            >
              What's new
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: isDarkMode ? "#a0aec0" : "#555",
                lineHeight: 1.75,
                whiteSpace: "pre-line",
              }}
            >
              { "Bug fixes and performance improvements."}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCloseUpdateModal}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                py: 1.2,
                borderColor: isDarkMode ? "#3d3d50" : "#e0e0e0",
                color: isDarkMode ? "#a0aec0" : "#555",
                "&:hover": {
                  borderColor: isDarkMode ? "#5a5a70" : "#bdbdbd",
                  backgroundColor: isDarkMode ? "#1a1f2e" : "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleUpdateNow}
              startIcon={<SystemUpdateAltIcon />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "#ffffff",
                py: 1.2,
                backgroundColor: "#219297",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1a7a7e",
                  boxShadow: "none",
                },
              }}
            >
              Update now
            </Button>
          </Stack>
        </Box>
      </>
    )}

  </DialogContent>
</Dialog>

<style>
  {`
    @keyframes popIn {
      0% { transform: scale(0.5); opacity: 0; }
      70% { transform: scale(1.15); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes indeterminate {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(350%); }
    }
  `}
</style>

      {isMobile && (
        <SoftBox mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
              borderRadius: "12px",
              padding: 2,
              border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
              transition: "background-color 0.3s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "1.1rem", fontWeight: 600, color: isDarkMode ? "#e2e8f0" : undefined }}
            >
              {filteredTabs[tabIndex]?.label || "Settings"}
            </Typography>
            <IconButton onClick={handleDrawerToggle}>
              <MenuIcon sx={{ color: isDarkMode ? "#e2e8f0" : undefined }} />
            </IconButton>
          </Box>
        </SoftBox>
      )}

      <SoftBox display="flex" mt={isMobile ? 1 : 2} sx={{ minHeight: "70vh" }}>
        {!isMobile && (
          <Box
            sx={{
              position: "sticky",
              top: 90,
              height: "auto",
              width: collapsed ? 75 : { md: 200, lg: 220 },
              minWidth: collapsed ? 75 : { md: 200, lg: 220 },
              transition: "all 0.3s ease",
              borderRight: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
              backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: collapsed ? "center" : "flex-start",
              mr: 2,
            }}
          >
            <SidebarContent />
          </Box>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 280,
                backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
              },
            }}
          >
            <SidebarContent isMobileDrawer={true} />
          </Drawer>
        )}

        <SoftBox
          flexGrow={1}
          sx={{
            width: isMobile ? "100%" : "auto",
            overflowY: "auto",
            maxHeight: "calc(100vh - 120px)",
          }}
        >
          <Collapse in={showBanners}>
            <Stack spacing={1} mb={2}>
              {pendingPagesCount > 0 && (
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
                    border: `1px solid #219297`,
                    borderLeft: "4px solid #219297",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    boxShadow: "0 1px 4px rgba(33, 146, 151, 0.1)",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: isDarkMode ? "#2d2d3f" : "#e3fdf9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {pagesProgress === 100 ? (
                        <CheckCircleIcon sx={{ color: "#219297", fontSize: 18 }} />
                      ) : (
                        <SyncIcon sx={{ color: "#219297", fontSize: 18 }} />
                      )}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: isDarkMode ? "#e2e8f0" : "#1a1a1a",
                          mb: 0.25,
                        }}
                      >
                        Web Pages Sync
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#a0aec0" : "#666" }}>
                        {pendingPagesCount} of {totalPagesCount} pages pending
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120, mr: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: isDarkMode ? "#2d2d3f" : "#e3fdf9",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${pagesProgress}%`,
                              backgroundColor: "#219297",
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#219297",
                            mt: 0.25,
                            textAlign: "right",
                          }}
                        >
                          {Math.round(pagesProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => fetchSyncStatus()}
                      disabled={syncLoading}
                      sx={{
                        backgroundColor: isDarkMode ? "#2d2d3f" : "#f5f5f5",
                        "&:hover": { backgroundColor: isDarkMode ? "#3d3d4f" : "#e0e0e0" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          fontSize: 16,
                          animation: syncLoading || isPolling ? "spin 1s linear infinite" : "none",
                          color: "#219297",
                        }}
                      />
                    </IconButton>
                  </Stack>
                </Box>
              )}

              {pendingMediaCount > 0 && (
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
                    border: `1px solid #219197`,
                    borderLeft: "4px solid #219197",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    boxShadow: "0 1px 4px rgba(33, 145, 151, 0.1)",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: isDarkMode ? "#2d2d3f" : "#e0fffc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {mediaProgress === 100 ? (
                        <CheckCircleIcon sx={{ color: "#219197", fontSize: 18 }} />
                      ) : (
                        <VideoLibraryIcon sx={{ color: "#219197", fontSize: 18 }} />
                      )}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: isDarkMode ? "#e2e8f0" : "#1a1a1a",
                          mb: 0.25,
                        }}
                      >
                        External Media Sync
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#a0aec0" : "#666" }}>
                        {pendingMediaCount} of {totalMediaCount} items pending
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120, mr: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: isDarkMode ? "#2d2d3f" : "#e0fffd",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${mediaProgress}%`,
                              backgroundColor: "#219197",
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#219197",
                            mt: 0.25,
                            textAlign: "right",
                          }}
                        >
                          {Math.round(mediaProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => fetchSyncStatus()}
                      disabled={syncLoading}
                      sx={{
                        backgroundColor: isDarkMode ? "#2d2d3f" : "#f5f5f5",
                        "&:hover": { backgroundColor: isDarkMode ? "#3d3d4f" : "#e0e0e0" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          fontSize: 16,
                          animation: syncLoading || isPolling ? "spin 1s linear infinite" : "none",
                          color: "#219197",
                        }}
                      />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Collapse>

          <Box
            sx={{
              backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
              borderRadius: "12px",
              minHeight: "20vh",
              border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
              p: 0,
              transition: "background-color 0.3s ease, border-color 0.3s ease",
            }}
          >
            {filteredTabs[tabIndex]?.component}
          </Box>
        </SoftBox>
      </SoftBox>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes popIn {
            0% { transform: scale(0.5); opacity: 0; }
            70% { transform: scale(1.15); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
          @keyframes indeterminate {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(350%); }
          }
        `}
      </style>
    </DashboardLayout>
  );
}